<template>
  <div
    class="section-selector-item group w-full flex items-center"
    :class="{ 'bg-primary5': isSelected }"
    :style="{ borderBottom: `1px solid transparent` }"
  >
    <div class="flex items-center w-full">
      <CInfoBadge
        v-if="shouldShowSelectionOrder"
        :background-color="themeVars.primary"
        class="h-9 w-9 mx-[5px] inline-flex justify-center items-center text-xs"
        :color="themeVars.base"
        :text="selectionIndex"
      />
      <NCheckbox
        v-else
        :checked="selectedSections.map((s) => s.id).includes(item.id as string)"
        class="h-9 w-9 inline-flex items-center justify-center cursor-pointer mx-[5px]"
        :value="item.id"
      />

      <div class="flex items-start gap-2 py-2 pr-2 b-rounded-2 w-full">
        <CThumbnail
          v-if="
            data[index].thumbnail_url || data[index].metadata?.thumbnail || !launchDarkly.enable_ai_generated_thumbnails
          "
          :height="75"
          :iconSize="24"
          :item="data[index]"
          :preview-disabled="false"
          :themeVars="themeVars"
          :thumbnailUrl="data[index].thumbnail_url ?? data[index].metadata?.thumbnail"
          :width="75"
          @click.stop
        />
        <CImage
          v-else
          :ai-img-description="item.name"
          class="rounded-2 overflow-hidden"
          :height="75"
          :img-props="{}"
          lazy
          :preview-disabled="false"
          :src="data[index].thumbnail_url ?? data[index].metadata?.thumbnail"
          :style="{
            border: `1px solid ${themeVars.primary6}`,
          }"
          :width="75"
          @click.stop
        />

        <div class="flex flex-wrap flex-col gap-2">
          <h4 class="flex flex-wrap font-700 font-size-[18px] overflow-hidden whitespace-normal break-words">
            {{ item.name }}
          </h4>
          <div class="flex flex-wrap gap-2">
            <CContentError v-if="data[index]?.access_type === 'restricted'" only-tag restricted />
            <NTag
              v-for="tag in data[index].tags?.filter?.(
                (t) => !HIDE_TAGS_WITH_PREFIX.some((prefix) => t.startsWith(prefix)),
              )"
              :key="tag"
              class="rounded-xl bg-primary6"
              size="small"
              :style="{
                '--n-border': 'none',
                '--n-text-color': themeVars.text,
              }"
            >
              {{ tag }}
            </NTag>
          </div>
          <CPopularityAndEngagementExtended
            v-if="Object.keys(popularityDict)?.length"
            class="gap-2 mr-2"
            :engagement="launchDarkly.show_popularity_icons ? popularityDict[data[index].id]?.engagement : undefined"
            :is-recommended="!!recommendedSectionIds.includes(data[index].id)"
            :popularity="launchDarkly.show_popularity_icons ? popularityDict[data[index].id]?.popularity : undefined"
            :views="launchDarkly.show_popularity_icons ? (popularityDict[data[index].id]?.views ?? 0) : 0"
          />
        </div>
      </div>

      <div
        v-if="$slots.actions"
        class="flex justify-end h-full items-center gap-1 cursor-auto py-3 px-4 relative"
        @click.stop
      >
        <slot name="actions"></slot>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { NCheckbox, NTag } from 'naive-ui'
import { computed, inject, type ComputedRef } from 'vue'

import CThumbnail from '@lib/components/CThumbnail/CThumbnail.vue'
import CImage from '@lib/components/CImage/CImage.vue'
import CInfoBadge from '@lib/components/CInfoBadge/CInfoBadge.vue'
import CContentError from '@lib/components/CContentError/CContentError.vue'
import CPopularityAndEngagementExtended from '@lib/components/CPopularityAndEngagementIcons/CPopularityAndEngagementExtended.vue'
import useRecommendations from '@canvas-builder/composables/useRecommendations'
import useSectionPopularity from '@canvas-builder/composables/useSectionPopularity'
import useThemeVars from '@lib/composables/useThemeVars'
import { HIDE_TAGS_WITH_PREFIX } from '@lib/constants/conventions.const'
import type { CanvasRetrieve } from '@openapi'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

const props = withDefaults(
  defineProps<{
    filters: Record<string, any>
    hasSequence?: boolean
    isSelected?: boolean
    index?: number
    item: { id: number | string; [key: string]: any }
    data: CanvasRetrieve[]
    selectedSections: CanvasRetrieve[]
  }>(),
  {
    hasSequence: false,
    index: 0,
    isSelected: false,
  },
)

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const themeVars = useThemeVars()
const filters = computed(() => props.filters)

const { popularityDict } = useSectionPopularity(filters)
const { recommendedSectionIds } = useRecommendations()

const shouldShowSelectionOrder = computed(
  () => !!launchDarkly.value.enable_selectors_dnd && props.isSelected && !props.hasSequence,
)

const selectionIndex = computed(() => {
  const index = props.selectedSections.findIndex((s) => s.id === props.item.id)
  return index !== -1 ? (index + 1).toString() : ''
})
</script>
